import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components'
import { Input, Table, Button, notification } from 'antd';
import { Icon, ThemeProvider } from 'dlweb-components';
import _ from 'lodash';
import ContextMenu from '../../ContextMenu';

const ActiveDirectoryGroupTable = ({isAdmin, defaultGroups, selectedGroups, setSelectedGroups, onClose, onOk}) => {

  let searchInput = null;

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState([]);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [menuCoordinates, setMenuCoordinates] = useState({});

  const rowSelection = {
    selectedRowKeys: selectedGroups.map(o => o.name),
    onSelect: (selectedKey, selected) => {
      setSelectedGroups(selected, false, [selectedKey]);
    },
    onSelectMultiple: (selected, selectedRows) => {
      setSelectedGroups(selected, false, selectedRows);
    },
    onSelectAll: (selected, selectedRows) => {
      setSelectedGroups(selected, false, !selected ? groups : selectedRows);
    }

  };

  const onRowClick = (e, record, index) => {

    let clearSelection = true;
    let selected = true;

    if(e.ctrlKey) {
      if(selectedGroups.length !== 1 && _.find(selectedGroups, record)) {
        selected = false;
      }

      clearSelection = false;
    }
    else if (e.shiftKey) {
      if(selectedGroups.length === 1 && selectedGroups[0].name === record.name) return;
      // Handle Shift key selection logic
      const lastSelectedIndex = selectedGroups.length > 0 ? selectedGroups[selectedGroups.length - 1].index : -1;
      const currentIndex = index; // Assuming record has an index property

      let range = [];
      if (lastSelectedIndex < currentIndex) {
          for (let i = lastSelectedIndex + 1; i <= currentIndex; i++) {
              range.push(groups[i]); // Assuming data is the array of all rows
          }
      } else {
          for (let i = currentIndex; i <= lastSelectedIndex - 1; i++) {
              range.push(groups[i]);
          }
      }

      setSelectedGroups([...selectedGroups, ...range], false, range);
      return;
    }
    else {
      if(_.find(selectedGroups, record) && selectedGroups.length === 1) return;
    }

    if (e.target.className.indexOf('ant-checkbox') > -1) return;
    
    setSelectedGroups(selected, clearSelection, [record])
  }
 
  const contextMenu = [{
    key: 'delete-group',
    action: 'delete-group',
    icon: <Icon type='users-slash' fixedWidth="25px" />,
    label: `Delete Group${selectedGroups.length > 1 ? 's' : ''}`
  }]

  const loadGroups = async (filters) => {
    const getADGroups = async () => {
      setLoading(true);
      const res = await axios.post('/api/wt/groupman/ad/list', { ...filters });

      if(res.data.errors.length > 0) {
        console.error(res.data.errors[0]);
        notification.error({message: 'Could not retrieve Active Directory groups'});
        onClose();
      }

      setLoading(false);
      setGroups(res.data.data);
      setPagination(p => ({ ...p, total: res.data._meta.totalRecords }));
    }

    getADGroups();
  }


  useEffect(() => {

    const cf = {
      limit: 50,
      skip: 0,
      order: [{
        column: 'name',
        direction: 0
      }]
    }

    loadGroups(cf);
  }, [])

  const openContextMenu = useCallback((e, record) => {

    e.preventDefault();

    if(!isAdmin) return;

    const isSelected = e.target.closest('tr').className.indexOf('ant-table-row-selected') > -1;
    
    setSelectedGroups(true, !isSelected, [record]);
    
    let coordinates = { x: e.pageX - 50, y: e.pageY - 50 };

    const isDefaultGroup = defaultGroups.includes(record.name);
    const isDefaultSelected = _.map(selectedGroups, 'name').some(i => defaultGroups.includes(i));

    if ((isDefaultGroup && !isSelected) || (isSelected && isDefaultSelected)) {
      coordinates = {};  
    }

    setMenuCoordinates(coordinates);
  }, [selectedGroups, defaultGroups, isAdmin])

  const getColumnSearchProps = (columnTitle, dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Filter ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 90, marginRight: 8 }}
        >
          Apply
        </Button>
        <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
  });

  const handleSearch = (dataIndex, selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  const columns = [
    {
      key: 'name',
      title: 'Group Name',
      dataIndex: 'name',
      sorter: true,
      ellipsis: true,
      defaultSortOrder: 'ascend',
      render: (text) =>
        <GroupName>
          <Icon type='users' fixedWidth="25px" style={{marginRight: '10px', color: '#99a1a8'}} />
          {text}
        </GroupName>,
      ...getColumnSearchProps('Group Name', 'name'),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {

    const pager = { ...pagination };
    pager.current = pagination.current;
    setPagination(pager);
        
    const filterObject = {};

    _.forEach(filters, (value, field) => {
      if(value) filterObject[field] = `${value[0]}*`
    })

    const cf = {
      limit: pagination.pageSize,
      skip: (pagination.current - 1) * pagination.pageSize,
      order: [{
        column: sorter.columnKey,
        direction: sorter.order === 'ascend' ? 0 : 1
      }],
      filter: filterObject
    }

    setCurrentFilters(cf)
    loadGroups(cf);
  };

  return(
    <ThemeProvider componentToken={{Table: { headerBorderRadius: 0}}}>
      <TableContainer>
        <Table
          rowKey="name"
          loading={loading}
          dataSource={groups}
          columns={columns}
          showSorterTooltip={false}
          size="small"
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          pagination={{
            total: pagination.total,
            showSizeChanger: false,
            defaultPageSize: 50,
            defaultCurrent: 1,
            position: ['bottomLeft']
          }}
          onChange={handleTableChange}
          onRow={(record, rowIndex) => {
            return {
              onClick: event => onRowClick(event, record, rowIndex),
              onContextMenu: event => openContextMenu(event, record),
            };
          }}
          scroll={{y: '400px'}}
        />
        {isAdmin &&
          <ContextMenu
            menuItems={contextMenu}
            coordinates={menuCoordinates}
            handleClose={() => setMenuCoordinates({})}
            onClick={onContextClick}
          />
        }
      </TableContainer>
    </ThemeProvider>
    )
}

const TableContainer = styled.div`
  user-select: none;
  .ant-table-body,
  .ant-table-tbody,
  .ant-table-content,
  .ant-table-container,
  .ant-table-wrapper,
  .ant-table-placeholder,
  .ant-table-expanded-row-fixed {
    overflow: hidden auto !important;
    /* min-height: calc(100vh - 186px); */
    min-height: 400px;
  }
  td.ant-table-column-sort {
    background: #fff;
  }
`

const GroupName = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-right: 10px;
`

export default ActiveDirectoryGroupTable;